import { api } from 'helpers'
import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico/endpoints'
import type {
  ArcProject,
  ArcProjectResponse,
  ProjectsQueryParams,
  ProjectStatus,
  PaginationMeta,
  PaginatedResponse,
  AssignProjectRequest,
  AssignProjectResponse,
  Tutor,
  ShareType,
} from '../types'

export interface FetchProjectsResult {
  projects: ArcProject[]
  pagination: PaginationMeta
}

/**
 * Transform API response to ArcProject
 * Note: API helper already converts snake_case → camelCase via camelizeKeys
 */
const transformProject = (project: ArcProjectResponse): ArcProject => ({
  id: String(project.id),
  name: project.name,
  status: project.status as ProjectStatus,
  synopsis: project.synopsis,
  groupSchoolTermId: project.groupSchoolTermId ? String(project.groupSchoolTermId) : null,
  schoolTermId: project.schoolTermId ? String(project.schoolTermId) : null,
  groupName: project.groupSchoolTerm?.name || null,
  kind: project.kind,
  updatedAt: project.updatedAt,
  schoolId: String(project.schoolId),
  authorId: String(project.authorId),
  projectGrades: Array.isArray(project.projectGrades) ? project.projectGrades : [],
  authorName: project.author
    ? `${project.author.firstName} ${project.author.lastName}`
    : undefined,
  coTeachers: (project.projectTeachers || []).map(pt => ({
    email: pt.tutor?.user?.email || '',
    firstName: pt.tutor?.user?.firstName || '',
    lastName: pt.tutor?.user?.lastName || '',
  })),
  categories: Array.isArray(project.categories) ? project.categories : [],
})

const buildQueryString = (params: ProjectsQueryParams): string => {
  const searchParams = new URLSearchParams()

  if (params.name) searchParams.set('name', params.name)
  if (params.status) searchParams.set('status', params.status)
  if (params.order_by) searchParams.set('order_by', params.order_by)
  if (params.direction) searchParams.set('direction', params.direction)
  if (params.page) searchParams.set('page', String(params.page))
  if (params.limit) searchParams.set('limit', String(params.limit))
  if (params.grade_range && params.grade_range !== 'all') {
    searchParams.set('grade_range', params.grade_range)
  }
  if (params.category_id) {
    searchParams.set('category_id', params.category_id)
  }
  if (params.grade_id) {
    searchParams.set('grade_id', params.grade_id)
  }

  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

export const fetchMyProjects = async (
  params: ProjectsQueryParams = {},
): Promise<FetchProjectsResult> => {
  const queryString = buildQueryString(params)
  const url = `${ARC_MOSAICO_ENDPOINTS.projects.list}${queryString}`

  const response = await api.get<PaginatedResponse<ArcProjectResponse>>(url)

  const projects = response.data?.projects
  if (!Array.isArray(projects)) {
    throw new Error('Invalid response from projects API')
  }

  return {
    projects: projects.map(transformProject),
    pagination: response.data.pagination,
  }
}

export const fetchLibraryProjects = async (
  params: ProjectsQueryParams = {},
): Promise<FetchProjectsResult> => {
  const queryString = buildQueryString(params)
  const url = `${ARC_MOSAICO_ENDPOINTS.projects.library}${queryString}`

  const response = await api.get<PaginatedResponse<ArcProjectResponse>>(url)

  const projects = response.data?.projects
  if (!Array.isArray(projects)) {
    throw new Error('Invalid response from library API')
  }

  return {
    projects: projects.map(transformProject),
    pagination: response.data.pagination,
  }
}

/**
 * Assign own project to students (My Projects: draft/published)
 * PATCH /api/arc_mosaico/projects/:id/assigned
 * Note: This MODIFIES the original project (template → application + approved)
 */
export const assignProject = async (
  projectId: string,
  groupSchoolTermId: string,
  studentIds: string[],
  startDate?: Date | null,
): Promise<AssignProjectResponse> => {
  const url = ARC_MOSAICO_ENDPOINTS.projects.assign(projectId)
  const payload: AssignProjectRequest = {
    group_school_term_id: groupSchoolTermId,
    student_ids: studentIds,
  }

  if (startDate) {
    const year = startDate.getFullYear()
    const month = String(startDate.getMonth() + 1).padStart(2, '0')
    const day = String(startDate.getDate()).padStart(2, '0')
    payload.start_date = `${year}-${month}-${day}`
  }

  const response = await api.patch<ArcProjectResponse>(url, payload)

  if (response.status === 401) {
    return { success: false, error: 'UNAUTHORIZED' }
  }

  if (response.status === 422) {
    const errorData = response.data as unknown as { error?: string }
    return { success: false, error: errorData.error || 'Validation error' }
  }

  return { success: true, project: response.data }
}

/**
 * Add published template to my projects (creates a copy)
 * GET /api/arc_mosaico/projects/:id/assigned_published_template
 */
export const assignPublishedTemplate = async (
  templateId: string,
): Promise<{ success: boolean; projectId?: string; error?: string }> => {
  const url = ARC_MOSAICO_ENDPOINTS.projects.assignPublishedTemplate(templateId)

  try {
    const response = await api.get<{ projectId: string }>(url)
    return { success: true, projectId: response.data.projectId }
  } catch (error: unknown) {
    const axiosError = error as { response?: { status?: number; data?: { error?: string } } }
    if (axiosError.response?.status === 401) {
      return { success: false, error: 'UNAUTHORIZED' }
    }
    return { success: false, error: axiosError.response?.data?.error || 'Failed to add template' }
  }
}

/**
 * Mark a project as completed
 * PUT /api/arc_mosaico/projects/:id/complete
 */
export const completeProject = async (projectId: string): Promise<{ success: boolean; error?: string }> => {
  const url = ARC_MOSAICO_ENDPOINTS.projects.complete(projectId)

  try {
    const response = await api.put(url)

    if (response.status === 401) {
      return { success: false, error: 'UNAUTHORIZED' }
    }

    if (response.status === 422) {
      const errorData = response.data as unknown as { error?: string }
      return { success: false, error: errorData.error || 'Validation error' }
    }

    if (response.status >= 200 && response.status < 300) {
      return { success: true }
    }

    return { success: false, error: 'Unexpected error' }
  } catch (error) {
    return { success: false, error: 'Network error' }
  }
}

/** Project teacher returned by GET /api/arc_mosaico/projects/:id/teachers */
export interface ProjectTeacher {
  id: string
  name: string
  educatorRole: string
  mainTeacher: boolean
}

/**
 * List project teachers (author + co-teachers)
 * GET /api/arc_mosaico/projects/:id/teachers
 */
export const listProjectTeachers = async (projectId: string): Promise<ProjectTeacher[]> => {
  const response = await api.get<ProjectTeacher[]>(ARC_MOSAICO_ENDPOINTS.projects.teachers(projectId))
  return Array.isArray(response.data) ? response.data : []
}

/**
 * List active tutors available for sharing (any tutor can access)
 * GET /api/arc_mosaico/projects/shareable_tutors
 */
export const listTutors = async (): Promise<Tutor[]> => {
  const response = await api.get<{ tutors: Tutor[] }>(ARC_MOSAICO_ENDPOINTS.projects.shareableTutors)
  return Array.isArray(response.data.tutors) ? response.data.tutors : []
}

/**
 * Update assigned students on an existing project
 * PATCH /api/arc_mosaico/projects/:id/update_students
 */
export const updateProjectStudents = async (
  projectId: string,
  studentIds: string[],
): Promise<{ success: boolean; error?: string }> => {
  const url = ARC_MOSAICO_ENDPOINTS.projects.updateStudents(projectId)

  try {
    const response = await api.patch(url, { student_ids: studentIds })

    if (response.status === 401) {
      return { success: false, error: 'UNAUTHORIZED' }
    }

    if (response.status === 422) {
      const errorData = response.data as unknown as { error?: string }
      return { success: false, error: errorData.error || 'Validation error' }
    }

    return { success: true }
  } catch (error: unknown) {
    const axiosError = error as { response?: { status?: number; data?: { error?: string } } }
    if (axiosError.response?.status === 401) {
      return { success: false, error: 'UNAUTHORIZED' }
    }
    if (axiosError.response?.status === 422) {
      const errorData = axiosError.response.data
      return { success: false, error: errorData?.error || 'Validation error' }
    }
    return { success: false, error: 'Network error' }
  }
}

/**
 * Share a project with tutors (copy or co-teacher)
 * POST /api/arc_mosaico/projects/:id/share
 */
export const shareProject = async (
  projectId: string,
  tutorIds: string[],
  type: ShareType,
): Promise<{ success: boolean; error?: string }> => {
  const url = ARC_MOSAICO_ENDPOINTS.projects.share(projectId)

  try {
    await api.post(url, { tutor_ids: tutorIds, type })
    return { success: true }
  } catch (error: unknown) {
    const axiosError = error as { response?: { status?: number; data?: unknown } }
    if (axiosError.response?.status === 422) {
      const data = axiosError.response.data
      const message = typeof data === 'string' ? data : 'Validation error'
      return { success: false, error: message }
    }
    return { success: false, error: 'Network error' }
  }
}
